import React, { useCallback, useEffect, useMemo, useState } from "react";
import MaterialReactTable, { type MRT_ColumnDef } from "material-react-table";
import {
  FolderFilled,
  DocumentTextRegular,
  ArrowDownloadRegular,
} from "@fluentui/react-icons";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Button,
} from "@fluentui/react-components";
import { MoreVerticalRegular } from "@fluentui/react-icons";
import { formatedDate } from "../../../../utils";
import apiServices from "../../../../service";
import ASCButton from "../../../../components/Button";
import "../style.css";

interface FileItem {
  type: "file";
  name: string;
  path: string;
  lastModified: string;
  metadata: any;
}

interface FolderItem {
  type: "folder";
  name: string;
  path: string;
  lastModified: string;
  children: (FolderItem | FileItem)[];
}

type Item = FolderItem | FileItem;

interface DocumentListProps {
  structure: Item[];
  searchResults: any[];
  search: string;
  navigateTo: (path: string[], isBreadcrumb?: boolean) => void;
  currentPath: string[];
  activeCustomerId: string;
  loading: boolean;
}

// Strip 13-digit timestamp prefix from file names (e.g. "1758120407923_filename.pdf")
function formatFileName(name: string): string {
  const match = name.match(/^\d{13}_(.+)$/);
  return match ? match[1] : name;
}

// Separate component so each row can own its popover state
function ActionsCell({
  item,
  activeCustomerId,
}: {
  item: Item;
  activeCustomerId: string;
}) {
  const [open, setOpen] = useState(false);

  if (item.type !== "file") return <span />;

  const handleDownload = () => {
    setOpen(false);
    apiServices.document
      .getDocument(item.metadata?.documentid, activeCustomerId)
      .then((res: any) => {
        if (res.data?.data) {
          const { fileUrl, DocumentTitle } = res.data.data;
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = DocumentTitle;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((err: any) => console.error(err));
  };

  return (
    <Popover
      open={open}
      positioning="below"
      onOpenChange={(_, data) => setOpen(data.open)}
    >
      <PopoverTrigger>
        <Button shape="rounded" icon={<MoreVerticalRegular />} />
      </PopoverTrigger>
      <PopoverSurface>
        <div className="doc-actions-menu">
          <ASCButton
            shape="rounded"
            icon={<ArrowDownloadRegular />}
            onClick={handleDownload}
            label="Download"
          />
        </div>
      </PopoverSurface>
    </Popover>
  );
}

const PAGE_SIZE = 20;

const DocumentList: React.FC<DocumentListProps> = ({
  structure,
  searchResults,
  search,
  navigateTo,
  currentPath,
  activeCustomerId,
  loading,
}) => {
  const allItems: Item[] = search ? searchResults : structure;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset visible count when folder/search changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [structure, searchResults, search]);

  const items = useMemo(() => allItems.slice(0, visibleCount), [allItems, visibleCount]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && visibleCount < allItems.length) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, allItems.length));
    }
  }, [visibleCount, allItems.length]);

  const columns = useMemo<MRT_ColumnDef<Item>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 240,
        Cell: ({ row }) => {
          const item = row.original;
          const displayName =
            item.type === "file" ? formatFileName(item.name) : item.name;

          const handleFolderClick = () => {
            if (search) {
              navigateTo(item.path.split("/").filter(Boolean));
            } else {
              navigateTo([...currentPath, item.name]);
            }
          };

          return (
            <div
              className={`doc-name-cell${item.type === "folder" ? " doc-folder-clickable" : ""}`}
              onClick={item.type === "folder" ? handleFolderClick : undefined}
              title={item.name}
            >
              {item.type === "folder" ? (
                <FolderFilled primaryFill="#fccc77" fontSize="20px" />
              ) : (
                <DocumentTextRegular primaryFill="#8c8c8c" fontSize="20px" />
              )}
              <span className="doc-item-name">{displayName}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 80,
        Cell: ({ row }) =>
          row.original.type === "folder" ? "Folder" : "File",
      },
      {
        accessorKey: "lastModified",
        header: "Date",
        size: 140,
        Cell: ({ row }) =>
          row.original.lastModified
            ? formatedDate(row.original.lastModified)
            : "—",
      },
      {
        accessorKey: "metadata.description",
        header: "Description",
        Cell: ({ row }) => {
          const item = row.original;
          if (item.type !== "file") return <span>—</span>;
          return (
            <span
              dangerouslySetInnerHTML={{
                __html: item.metadata?.description || "—",
              }}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <ActionsCell
            item={row.original}
            activeCustomerId={activeCustomerId}
          />
        ),
      },
    ],
    [search, currentPath, navigateTo, activeCustomerId]
  );

  return (
    <div className="doc-mgmt-table-wrapper">
      <MaterialReactTable
        columns={columns}
        data={items}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnActions={false}
        enableSorting={true}
        enableFilters={false}
        enablePagination={false}
        enableColumnResizing={false}
        enableStickyHeader={true}
        enableRowSelection={false}
        state={{
          isLoading: loading && items.length === 0,
          showProgressBars: loading && items.length > 0,
        }}
        muiTableContainerProps={{
          sx: { maxHeight: "calc(100vh - 300px)", overflow: "auto" },
          onScroll: handleScroll,
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 600,
            fontSize: "1rem",
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #ddd",
            position: "sticky",
            top: 0,
            zIndex: 1,
          },
        }}
        muiTableBodyRowProps={{
          sx: { "&:hover td": { backgroundColor: "#f5f5f5" } },
        }}
        muiTableBodyCellProps={{ sx: { fontSize: "1rem" } }}
        localization={{ noRecordsToDisplay: "This folder is empty" }}
        initialState={{ density: "compact" }}
      />
    </div>
  );
};

export default DocumentList;
